import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { IdrSlider } from "@/components/idr/IdrSlider";
import { IDR_ITEMS, IDR_SCALE } from "@/data/idr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 10;
const TOTAL_PAGES = Math.ceil(IDR_ITEMS.length / PER_PAGE);
const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

const T = {
  fr: {
    loading: "Chargement…",
    invalidTitle: "Lien invalide",
    invalidText: "Ce lien n'est pas valide ou a expiré. Merci de redemander un lien à la personne qui vous a invité(e).",
    badge: "Le regard d'un proche",
    introTitle: "Comment percevez-vous cette personne\u00a0?",
    intro1: "Une personne proche de vous a réalisé l'Inventaire des Drivers Relationnels et souhaite recueillir votre regard.",
    intro2: "Vous allez lire 50 affirmations. Pour chacune, indiquez à quel point elle décrit cette personne, selon vous — spontanément, sans chercher la « bonne réponse ». Vos réponses lui seront transmises\u00a0; seul(e) elle verra la comparaison.",
    nameLabel: "Votre prénom (facultatif)",
    namePh: "Votre prénom",
    start: "Commencer",
    anchorLeft: "Ne lui correspond pas du tout",
    anchorRight: "Lui correspond totalement",
    sliderHint: "Faites glisser ou cliquez sur un point pour répondre",
    step: "Étape",
    of: "sur",
    prev: "Précédent",
    next: "Suivant",
    finish: "Transmettre mon regard",
    sending: "Envoi…",
    completeStep: "Merci de répondre à toutes les affirmations de cette étape pour continuer.",
    thanks: (n) => `Merci${n ? `, ${n}` : ""} !`,
    thanksText: "Votre regard a bien été transmis. La personne qui vous a invité(e) pourra le comparer avec son propre inventaire.",
    close: "Vous pouvez maintenant fermer cette page.",
    errorSend: "Échec de l'envoi — réessayez.",
  },
  es: {
    loading: "Cargando…",
    invalidTitle: "Enlace no válido",
    invalidText: "Este enlace no es válido o ha caducado. Solicite un nuevo enlace a la persona que le invitó.",
    badge: "La mirada de un allegado",
    introTitle: "¿Cómo percibe a esta persona?",
    intro1: "Una persona cercana a usted ha realizado el Inventario de Drivers Relacionales y desea recoger su mirada.",
    intro2: "Va a leer 50 afirmaciones. Para cada una, indique en qué medida describe a esta persona, según usted — con espontaneidad, sin buscar la «respuesta correcta». Sus respuestas le serán transmitidas; solo ella verá la comparación.",
    nameLabel: "Su nombre (opcional)",
    namePh: "Su nombre",
    start: "Empezar",
    anchorLeft: "No le corresponde en absoluto",
    anchorRight: "Le corresponde totalmente",
    sliderHint: "Desliza o haz clic en un punto para responder",
    step: "Etapa",
    of: "de",
    prev: "Anterior",
    next: "Siguiente",
    finish: "Transmitir mi mirada",
    sending: "Enviando…",
    completeStep: "Responde a todas las afirmaciones de esta etapa para continuar.",
    thanks: (n) => `¡Gracias${n ? `, ${n}` : ""}!`,
    thanksText: "Su mirada se ha transmitido correctamente. La persona que le invitó podrá compararla con su propio inventario.",
    close: "Ahora puede cerrar esta página.",
    errorSend: "Error al enviar — inténtelo de nuevo.",
  },
};

const Intro = ({ t, name, setName, onStart }) => (
  <div className="animate-fade-up text-center">
    <p className="text-xs uppercase tracking-[0.3em] text-[var(--gold-light)] mb-4">{t.badge}</p>
    <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] leading-tight mb-6">{t.introTitle}</h1>
    <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto mb-4">{t.intro1}</p>
    <p className="text-[var(--ink-soft)] text-sm max-w-xl mx-auto mb-8">{t.intro2}</p>
    <div className="rounded-2xl bg-white/70 border border-[var(--border)] p-5 max-w-md mx-auto mb-8 text-left">
      <label className="block text-sm text-[var(--ink)] mb-2 font-medium">{t.nameLabel}</label>
      <input value={name} onChange={(e) => setName(e.target.value)} data-testid="idr-entourage-name-input" placeholder={t.namePh}
        className="w-full rounded-xl border border-[var(--border)] bg-[var(--sand)] p-3 focus:outline-none focus:border-[var(--gold)]" />
    </div>
    <button onClick={onStart} data-testid="idr-entourage-btn-start" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{t.start}</button>
  </div>
);

const ItemsPage = ({ t, lang, scale, page, answers, onAnswer, onPrev, onNext, sending }) => {
  const items = IDR_ITEMS.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  const answeredCount = Object.keys(answers).filter((k) => Number.isFinite(answers[k])).length;
  const pct = Math.round((answeredCount / IDR_ITEMS.length) * 100);
  const pageComplete = items.every((it) => Number.isFinite(answers[it.n]));
  const last = page === TOTAL_PAGES - 1;

  return (
    <div className="animate-fade-up">
      <div className="mb-8" data-testid="idr-entourage-progress">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm uppercase tracking-[0.18em] text-[var(--ink-soft)]">{t.step} {page + 1} {t.of} {TOTAL_PAGES}</span>
          <span className="font-serif-title text-2xl text-[var(--gold)]">{pct} %</span>
        </div>
        <div className="h-3 w-full rounded-full bg-[var(--beige)] overflow-hidden">
          <div className="h-full rounded-full bg-[var(--gold)] transition-all duration-500 ease-out" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="space-y-4">
        {items.map((it) => (
          <IdrSlider key={it.n} id={`idr-ent-${it.n}`} statement={`« ${it[lang]} »`} index={it.n} scale={scale} value={answers[it.n]} onChange={(v) => onAnswer(it.n, v)} anchorLeft={t.anchorLeft} anchorRight={t.anchorRight} hint={t.sliderHint} />
        ))}
      </div>

      <div className="flex items-center justify-between mt-8">
        <button onClick={onPrev} data-testid="idr-entourage-btn-prev" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{t.prev}</button>
        <button onClick={onNext} disabled={!pageComplete || sending} data-testid="idr-entourage-btn-next"
          className={`px-8 py-3 rounded-full font-medium transition-all ${pageComplete && !sending ? "bg-[var(--gold)] text-white hover:brightness-105 shadow-sm" : "bg-[var(--beige)] text-[var(--ink-soft)] cursor-not-allowed"}`}>
          {last ? (sending ? t.sending : t.finish) : t.next}
        </button>
      </div>
      {!pageComplete && <p className="text-center text-xs text-[var(--ink-soft)] mt-3">{t.completeStep}</p>}
    </div>
  );
};

const Done = ({ t, name }) => (
  <div className="animate-fade-up text-center py-8" data-testid="idr-entourage-done">
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 sm:p-12">
      <h2 className="font-serif-title text-4xl text-[var(--gold)] mb-4">{t.thanks(name)}</h2>
      <p className="text-[var(--ink)] leading-relaxed max-w-md mx-auto mb-4">{t.thanksText}</p>
      <p className="text-xs text-[var(--ink-soft)] mt-5">{t.close}</p>
    </div>
  </div>
);

export default function IdrEntourageModule() {
  const { token } = useParams();
  const [lang, setLang] = useState("fr");
  const [phase, setPhase] = useState("loading"); // loading | invalid | intro | items | done
  const [page, setPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const t = T[lang];
  const scale = IDR_SCALE[lang];

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await axios.get(`${API}/idr-entourage/proche/${token}`).then((r) => r.data);
        if (!active) return;
        if (!data.found) { setPhase("invalid"); return; }
        if (data.lang === "es") setLang("es");
        setPhase("intro");
      } catch {
        if (active) setPhase("invalid");
      }
    })();
    return () => { active = false; };
  }, [token]);

  const onAnswer = (n, v) => setAnswers((prev) => ({ ...prev, [n]: v }));

  const submit = async () => {
    setSending(true);
    try {
      const out = await axios.post(`${API}/idr-entourage/proche/${token}/submit`, {
        proche_name: name, proche_answers: answers,
      }).then((r) => r.data);
      if (!out.found) { toast.error(lang === "es" ? "Enlace no encontrado" : "Lien introuvable"); setSending(false); return; }
      setPhase("done"); setSending(false); scrollTop();
    } catch {
      toast.error(t.errorSend);
      setSending(false);
    }
  };

  const nextPage = () => { if (page < TOTAL_PAGES - 1) { setPage((p) => p + 1); scrollTop(); } else { submit(); } };
  const prevPage = () => { if (page > 0) { setPage((p) => p - 1); scrollTop(); } else { setPhase("intro"); scrollTop(); } };

  const render = () => {
    switch (phase) {
      case "loading":
        return <div className="text-center py-20 text-[var(--ink-soft)]" data-testid="idr-entourage-loading">{t.loading}</div>;
      case "invalid":
        return (
          <div className="text-center py-20 animate-fade-up" data-testid="idr-entourage-invalid">
            <h1 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-3">{t.invalidTitle}</h1>
            <p className="text-[var(--ink-soft)] max-w-md mx-auto leading-relaxed">{t.invalidText}</p>
          </div>
        );
      case "intro":
        return <Intro t={t} name={name} setName={setName} onStart={() => { setPhase("items"); setPage(0); scrollTop(); }} />;
      case "items":
        return <ItemsPage t={t} lang={lang} scale={scale} page={page} answers={answers} onAnswer={onAnswer} onPrev={prevPage} onNext={nextPage} sending={sending} />;
      default:
        return <Done t={t} name={name} />;
    }
  };

  return (
    <Layout>
      <div className="flex justify-end mb-4">
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>
      {render()}
    </Layout>
  );
}
